import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { 
  UserCog, Edit2, UserPlus, Mail, Clock, Trash2, Eye, EyeOff, Key,
  RefreshCw, CheckCircle2, XCircle, Download
} from "lucide-react";
import { NestIcon } from "../../components/NestIcon";
import { AgentLayout } from "./AgentDashboard";
import { API } from "../../App";
import axios from "axios";
import { toast } from "sonner";

export default function AgentTeam() {
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [roles, setRoles] = useState(null);
  const [currentAgent, setCurrentAgent] = useState(null);
  const [editAgent, setEditAgent] = useState(null);
  const [newRole, setNewRole] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  
  // Add team member state
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [addForm, setAddForm] = useState({
    email: "",
    password: "",
    full_name: "",
    role: "agent"
  });
  const [showPassword, setShowPassword] = useState(false);
  const [adding, setAdding] = useState(false);
  
  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Password reset
  const [resetPasswordAgent, setResetPasswordAgent] = useState(null);
  const [manualPassword, setManualPassword] = useState("");
  const [sendPasswordEmail, setSendPasswordEmail] = useState(true);
  
  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [bulkRoleDialogOpen, setBulkRoleDialogOpen] = useState(false);
  const [bulkNewRole, setBulkNewRole] = useState("");
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const [teamRes, rolesRes, meRes] = await Promise.all([
        axios.get(`${API}/agent/team`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/agent/roles`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/agent/me`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      setAgents(teamRes.data);
      setRoles(rolesRes.data);
      setCurrentAgent(meRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("You don't have permission to view team members");
      } else {
        toast.error("Failed to load team data");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRole = async () => {
    if (!editAgent || !newRole) return;

    const token = localStorage.getItem("agent_token");
    try {
      await axios.put(
        `${API}/agent/team/${editAgent.agent_id}/role`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Role updated to ${newRole}`);
      setIsDialogOpen(false);
      setEditAgent(null);
      setNewRole("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
    }
  };

  const handleAddTeamMember = async () => {
    if (!addForm.email || !addForm.password || !addForm.full_name) {
      toast.error("Please fill in all required fields");
      return;
    }

    setAdding(true);
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.post(
        `${API}/agent/team`,
        addForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Team member ${response.data.full_name} created successfully!`);
      setIsAddDialogOpen(false);
      setAddForm({ email: "", password: "", full_name: "", role: "agent" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create team member");
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteTeamMember = async (agentId) => {
    const token = localStorage.getItem("agent_token");
    try {
      await axios.delete(`${API}/agent/team/${agentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Team member removed");
      setDeleteConfirm(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove team member");
    }
  };

  const handleResetPassword = async (agentId) => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.post(`${API}/agent/team/${agentId}/reset-password`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Temporary password: ${response.data.temporary_password}`, { duration: 10000 });
      setResetPasswordAgent(null);
      setManualPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    }
  };

  const handleSetPassword = async (agentId) => {
    if (manualPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    const token = localStorage.getItem("agent_token");
    try {
      await axios.post(`${API}/agent/team/${agentId}/set-password`, {
        new_password: manualPassword,
        send_email: sendPasswordEmail
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Password set successfully${sendPasswordEmail ? ' and email sent' : ''}`);
      setResetPasswordAgent(null);
      setManualPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to set password");
    }
  };

  // Permission helpers - defined before use
  const canManageAgents = () => {
    return currentAgent && ["super_admin", "admin"].includes(currentAgent.role);
  };

  const canManageRole = (targetRole) => {
    if (!roles || !currentAgent) return false;
    const currentLevel = roles.hierarchy[currentAgent.role] || 0;
    const targetLevel = roles.hierarchy[targetRole] || 0;
    return currentLevel > targetLevel;
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      super_admin: "bg-purple-100 text-purple-800 border-purple-300",
      admin: "bg-red-100 text-red-800 border-red-300",
      manager: "bg-blue-100 text-blue-800 border-blue-300",
      hr: "bg-teal-100 text-teal-800 border-teal-300",
      power_user: "bg-green-100 text-green-800 border-green-300",
      claims_processor: "bg-amber-100 text-amber-800 border-amber-300",
      agent: "bg-gray-100 text-gray-800 border-gray-300"
    };
    return colors[role] || "bg-gray-100 text-gray-800";
  };

  // Bulk selection handlers
  const selectableAgents = agents.filter(a => 
    a.agent_id !== currentAgent?.agent_id && canManageRole(a.role)
  );
  
  const toggleSelectAll = () => {
    if (selectedIds.length === selectableAgents.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(selectableAgents.map(a => a.agent_id));
    }
  };

  const toggleSelect = (agentId) => {
    setSelectedIds(prev => 
      prev.includes(agentId) 
        ? prev.filter(id => id !== agentId)
        : [...prev, agentId]
    );
  };

  const isAllSelected = selectableAgents.length > 0 && selectedIds.length === selectableAgents.length;
  const isSomeSelected = selectedIds.length > 0;

  // Bulk actions
  const handleBulkChangeRole = async () => {
    if (!bulkNewRole) {
      toast.error("Please select a role");
      return;
    }
    
    setBulkActionLoading(true);
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.post(
        `${API}/agent/team/bulk`,
        { ids: selectedIds, action: "change_role", params: { role: bulkNewRole } },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Updated role for ${response.data.success} team members`);
      setBulkRoleDialogOpen(false);
      setBulkNewRole("");
      setSelectedIds([]);
      fetchData();
    } catch (error) {
      toast.error("Failed to change roles");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkActivate = async () => {
    setBulkActionLoading(true);
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.post(
        `${API}/agent/team/bulk`,
        { ids: selectedIds, action: "activate" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Activated ${response.data.success} team members`);
      setSelectedIds([]);
      fetchData();
    } catch (error) {
      toast.error("Failed to activate team members");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDeactivate = async () => {
    setBulkActionLoading(true);
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.post(
        `${API}/agent/team/bulk`,
        { ids: selectedIds, action: "deactivate" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Deactivated ${response.data.success} team members`);
      setSelectedIds([]);
      fetchData();
    } catch (error) {
      toast.error("Failed to deactivate team members");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkResetPassword = async () => {
    setBulkActionLoading(true);
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.post(
        `${API}/agent/team/bulk`,
        { ids: selectedIds, action: "reset_password" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Reset passwords for ${response.data.success} team members. Emails sent.`);
      setSelectedIds([]);
    } catch (error) {
      toast.error("Failed to reset passwords");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    setBulkActionLoading(true);
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.post(
        `${API}/agent/team/bulk`,
        { ids: selectedIds, action: "delete" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Deleted ${response.data.success} team members`);
      setBulkDeleteDialogOpen(false);
      setSelectedIds([]);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete team members");
    } finally {
      setBulkActionLoading(false);
    }
  };

  if (loading) {
    return (
      <AgentLayout title="Team">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AgentLayout>
    );
  }

  return (
    <AgentLayout title="Team">
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Young Serif, serif' }}>
            Team Management
          </h1>
          <p className="text-muted-foreground">Manage agent roles and permissions</p>
        </div>
        
        {/* Add Team Member Button */}
        {canManageAgents() && (
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="add-team-member-btn">
                <UserPlus className="h-4 w-4" />
                Add Team Member
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Young Serif, serif' }}>
                  Add New Team Member
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div>
                  <Label>Full Name *</Label>
                  <Input
                    placeholder="John Smith"
                    value={addForm.full_name}
                    onChange={(e) => setAddForm({...addForm, full_name: e.target.value})}
                    data-testid="add-member-name"
                  />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    placeholder="john@company.com"
                    value={addForm.email}
                    onChange={(e) => setAddForm({...addForm, email: e.target.value})}
                    data-testid="add-member-email"
                  />
                </div>
                <div>
                  <Label>Password *</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={addForm.password}
                      onChange={(e) => setAddForm({...addForm, password: e.target.value})}
                      data-testid="add-member-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label>Role</Label>
                  <Select value={addForm.role} onValueChange={(val) => setAddForm({...addForm, role: val})}>
                    <SelectTrigger className="mt-2" data-testid="add-member-role">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      {roles && Object.keys(roles.hierarchy)
                        .filter(role => canManageRole(role))
                        .map(role => (
                          <SelectItem key={role} value={role}>
                            {role.replace('_', ' ')} (Level {roles.hierarchy[role]})
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button 
                  onClick={handleAddTeamMember}
                  disabled={adding}
                  className="w-full"
                  data-testid="submit-add-member"
                >
                  {adding ? "Creating..." : "Create Team Member"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Bulk Actions Bar */}
      {canManageAgents() && isSomeSelected && (
        <div className="mb-6 flex flex-wrap items-center gap-2 bg-primary/5 px-4 py-3 rounded-lg border border-primary/20">
          <Badge variant="secondary" className="mr-2">
            {selectedIds.length} selected
          </Badge>
          <Button 
            size="sm" 
            variant="outline"
            onClick={() => setBulkRoleDialogOpen(true)}
            disabled={bulkActionLoading}
            data-testid="bulk-change-role-btn"
          >
            <Edit2 className="h-4 w-4 mr-1" />
            Change Role
          </Button>
          <Button 
            size="sm" 
            variant="outline"
            onClick={handleBulkActivate}
            disabled={bulkActionLoading}
            data-testid="bulk-activate-btn"
          >
            <CheckCircle2 className="h-4 w-4 mr-1" />
            Activate
          </Button>
          <Button 
            size="sm" 
            variant="outline"
            onClick={handleBulkDeactivate}
            disabled={bulkActionLoading}
            data-testid="bulk-deactivate-btn"
          >
            <XCircle className="h-4 w-4 mr-1" />
            Deactivate
          </Button>
          <Button 
            size="sm" 
            variant="outline"
            onClick={handleBulkResetPassword}
            disabled={bulkActionLoading}
            data-testid="bulk-reset-password-btn"
          >
            <Key className="h-4 w-4 mr-1" />
            Reset Passwords
          </Button>
          <Button 
            size="sm" 
            variant="destructive"
            onClick={() => setBulkDeleteDialogOpen(true)}
            disabled={bulkActionLoading}
            data-testid="bulk-delete-btn"
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Delete
          </Button>
        </div>
      )}

      {/* Role Hierarchy Info */}
      {roles && (
        <Card className="card-organic mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Young Serif, serif' }}>
              <NestIcon className="h-5 w-5 text-primary" />
              Role Hierarchy
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {Object.entries(roles.hierarchy)
                .sort((a, b) => b[1] - a[1])
                .map(([role, level]) => (
                  <Badge 
                    key={role} 
                    className={`${getRoleBadgeColor(role)} border`}
                  >
                    {role.replace('_', ' ')} ({level})
                  </Badge>
                ))}
            </div>
            <p className="text-sm text-muted-foreground mt-3">
              Your role: <span className="font-semibold capitalize">{currentAgent?.role?.replace('_', ' ')}</span>
            </p>
          </CardContent>
        </Card>
      )}

      {/* Team Members */}
      <Card className="card-organic overflow-hidden">
        <CardContent className="p-0">
          {agents.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    {canManageAgents() && (
                      <th className="w-12">
                        <Checkbox
                          checked={isAllSelected}
                          onCheckedChange={toggleSelectAll}
                          disabled={selectableAgents.length === 0}
                          data-testid="select-all-checkbox"
                        />
                      </th>
                    )}
                    <th>Agent</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Joined</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {agents.map((agent) => {
                    const isSelectable = agent.agent_id !== currentAgent?.agent_id && canManageRole(agent.role);
                    return (
                      <tr key={agent.agent_id} className={selectedIds.includes(agent.agent_id) ? "bg-primary/5" : ""}>
                        {canManageAgents() && (
                          <td>
                            {isSelectable ? (
                              <Checkbox
                                checked={selectedIds.includes(agent.agent_id)}
                                onCheckedChange={() => toggleSelect(agent.agent_id)}
                                data-testid={`select-${agent.agent_id}`}
                              />
                            ) : null}
                          </td>
                        )}
                        <td>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                              <span className="text-primary font-semibold">
                                {agent.full_name?.charAt(0) || agent.email?.charAt(0) || '?'}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium">{agent.full_name || "N/A"}</p>
                              <p className="text-xs font-mono text-muted-foreground">{agent.agent_id}</p>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            {agent.email}
                          </div>
                        </td>
                        <td>
                          <Badge className={`${getRoleBadgeColor(agent.role)} border`}>
                            {agent.role?.replace('_', ' ')}
                          </Badge>
                        </td>
                        <td className="text-sm text-muted-foreground">
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            {agent.created_at ? new Date(agent.created_at).toLocaleDateString() : "N/A"}
                          </div>
                        </td>
                        <td>
                          {canManageRole(agent.role) && agent.agent_id !== currentAgent?.agent_id && (
                            <Dialog open={isDialogOpen && editAgent?.agent_id === agent.agent_id} onOpenChange={(open) => {
                              setIsDialogOpen(open);
                              if (!open) {
                                setEditAgent(null);
                                setNewRole("");
                              }
                            }}>
                              <DialogTrigger asChild>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => {
                                    setEditAgent(agent);
                                    setNewRole(agent.role);
                                    setIsDialogOpen(true);
                                  }}
                                  data-testid={`edit-agent-${agent.agent_id}`}
                                >
                                  <Edit2 className="h-4 w-4 mr-1" />
                                  Edit Role
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle style={{ fontFamily: 'Young Serif, serif' }}>
                                    Update Agent Role
                                  </DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4 pt-4">
                                  <div>
                                    <Label>Agent</Label>
                                    <p className="text-sm font-medium">{editAgent?.full_name || editAgent?.email}</p>
                                  </div>
                                  <div>
                                    <Label>Current Role</Label>
                                    <Badge className={`${getRoleBadgeColor(editAgent?.role || '')} border ml-2`}>
                                      {editAgent?.role?.replace('_', ' ')}
                                    </Badge>
                                  </div>
                                  <div>
                                    <Label>New Role</Label>
                                    <Select value={newRole} onValueChange={setNewRole}>
                                      <SelectTrigger className="mt-2">
                                        <SelectValue placeholder="Select role" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {roles && Object.keys(roles.hierarchy)
                                          .filter(role => canManageRole(role) || role === editAgent?.role)
                                          .map(role => (
                                            <SelectItem key={role} value={role}>
                                              {role.replace('_', ' ')} (Level {roles.hierarchy[role]})
                                            </SelectItem>
                                          ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <Button 
                                    onClick={handleUpdateRole} 
                                    className="w-full"
                                    disabled={newRole === editAgent?.role}
                                  >
                                    Update Role
                                  </Button>
                                </div>
                              </DialogContent>
                            </Dialog>
                          )}
                          {canManageAgents() && canManageRole(agent.role) && agent.agent_id !== currentAgent?.agent_id && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="ml-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                onClick={() => setResetPasswordAgent(agent)}
                                title="Reset Password"
                                data-testid={`reset-pwd-${agent.agent_id}`}
                              >
                                <Key className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => setDeleteConfirm(agent.agent_id)}
                                data-testid={`delete-agent-${agent.agent_id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          {agent.agent_id === currentAgent?.agent_id && (
                            <Badge variant="outline">You</Badge>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <UserCog className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No team members found or insufficient permissions</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Permissions Reference */}
      {roles && (
        <Card className="card-organic mt-6">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>
              Permissions Reference
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(roles.permissions).map(([role, permissions]) => (
                <div key={role} className="bg-muted/50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className={`${getRoleBadgeColor(role)} border`}>
                      {role.replace('_', ' ')}
                    </Badge>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {permissions.map((perm, idx) => (
                      <span key={idx} className="text-xs bg-background px-2 py-1 rounded">
                        {perm.replace('_', ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Young Serif, serif' }}>Remove Team Member?</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            Are you sure you want to remove this team member? They will no longer be able to access the agent portal.
          </p>
          <div className="flex gap-2 mt-4">
            <Button 
              variant="destructive" 
              onClick={() => handleDeleteTeamMember(deleteConfirm)} 
              className="flex-1"
              data-testid="confirm-delete-member"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Remove
            </Button>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={!!resetPasswordAgent} onOpenChange={(open) => !open && setResetPasswordAgent(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Young Serif, serif' }}>Reset Password</DialogTitle>
            <DialogDescription>
              Reset password for <strong>{resetPasswordAgent?.full_name || resetPasswordAgent?.email}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-col gap-3">
              <Button 
                onClick={() => handleResetPassword(resetPasswordAgent?.agent_id)} 
                className="w-full justify-start"
                variant="outline"
                data-testid="generate-temp-password"
              >
                <RefreshCw className="h-4 w-4 mr-3" />
                <div className="text-left">
                  <p className="font-medium">Generate Temporary Password</p>
                  <p className="text-xs text-muted-foreground">System will create a random password and email it</p>
                </div>
              </Button>
              
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">Or set manually</span>
                </div>
              </div>
              
              <div className="space-y-3 p-3 border rounded-lg">
                <div className="space-y-2">
                  <Label>New Password</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={manualPassword}
                      onChange={(e) => setManualPassword(e.target.value)}
                      placeholder="Enter new password (min 6 chars)"
                      data-testid="manual-password-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="sendEmail"
                    checked={sendPasswordEmail}
                    onCheckedChange={setSendPasswordEmail}
                  />
                  <Label htmlFor="sendEmail" className="text-sm cursor-pointer">
                    Send email with new password
                  </Label>
                </div>
                <Button 
                  onClick={() => handleSetPassword(resetPasswordAgent?.agent_id)} 
                  className="w-full"
                  disabled={manualPassword.length < 6}
                  data-testid="set-password-btn"
                >
                  <Key className="h-4 w-4 mr-2" />
                  Set Password
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResetPasswordAgent(null); setManualPassword(""); }}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Role Change Dialog */}
      <Dialog open={bulkRoleDialogOpen} onOpenChange={setBulkRoleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Role for {selectedIds.length} Members</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label>New Role</Label>
            <Select value={bulkNewRole} onValueChange={setBulkNewRole}>
              <SelectTrigger className="mt-2" data-testid="bulk-role-select">
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent>
                {roles && Object.keys(roles.hierarchy)
                  .filter(role => canManageRole(role))
                  .map(role => (
                    <SelectItem key={role} value={role}>
                      {role.replace('_', ' ')} (Level {roles.hierarchy[role]})
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkRoleDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleBulkChangeRole} disabled={bulkActionLoading || !bulkNewRole} data-testid="confirm-bulk-role-btn">
              {bulkActionLoading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Edit2 className="h-4 w-4 mr-2" />}
              Change Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {selectedIds.length} Team Members?</DialogTitle>
            <DialogDescription>
              This action cannot be undone. Selected members will be permanently removed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleBulkDelete} disabled={bulkActionLoading} data-testid="confirm-bulk-delete-btn">
              {bulkActionLoading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AgentLayout>
  );
}
